import type { TranslationKey } from "@privacy/translations";
import { type FC } from "react";
import { Checkbox } from "@privacy/ui";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { logUnhandled } from "../../../api/logError";
import { useGetUsersRoleAssignments, useUpdateRoleOnTenant } from "../../../api/users";
import { PageSpinner, PageTitle, PageWrapper, PAGE_PADDING_X, PAGE_PADDING_Y } from "../../../shared/Page";
import { Grid } from "../../../shared/Grid";
import { type Role, type UserRole } from "../../../api/users.types";
import type { DisplayedRole } from "../Roles";
import {
  DISPLAYED_ROLES,
  getRoleGridColumnWidth,
  RoleGridColumnHeader,
  ROLES_CONTROLLED_BY_ROOT,
  roleTexts,
} from "../Roles";
import { UserResponsibleWarningDialog } from "../UserGridPage/UserResponsibleWarningDialog";
import { usePrivacyDocumentTitle } from "../../../shared/usePrivacyDocumentTitle";
import { LosePermissionToUpdateRolesDialog } from "../UserGridPage/LosePermissionToUpdateRolesDialog";
import { PersonIcon2 } from "../../../shared/icons";
import { multilineEllipsis } from "../../../shared/multilineEllipsis";
import { useParsedParams } from "../../../shared/useParsedParams";
import { z } from "zod";
import { CreateUserButton } from "./CreateUser";
import { useScimEditingMode } from "../shared/useScimEditingMode";

type OnRoleChange = ({ value, role, userId }: { value: boolean; role: Role; userId: string }) => void;

const HEADER_HEIGHT = "66px";

export const OrganisationUnitUsers: FC<{ rootId: string }> = ({ rootId }) => {
  usePrivacyDocumentTitle("Sidebar.Tenants");
  const { t } = useTranslation();
  const parsedParams = useParsedParams(z.object({ tenantId: z.string() }));
  const updateRoleOnTenant = useUpdateRoleOnTenant();
  const { data } = useGetUsersRoleAssignments(parsedParams.tenantId);
  const { isScimEnabled, badge } = useScimEditingMode();

  const handleRoleChange: OnRoleChange = ({ value, userId, role }) => {
    if (!parsedParams.tenantId) throw new Error('"params.tenantId" has to be defined');

    updateRoleOnTenant.mutate({
      userId,
      tenantId: parsedParams.tenantId,
      payload: { tenantId: parsedParams.tenantId, role, action: value ? "grant" : "revoke" },
    });
  };

  if (!data) return <PageSpinner />;

  const { users, tenantName } = data;

  const roleColumns = DISPLAYED_ROLES.map(columnRole =>
    roleColumn({
      columnRole,
      onRoleChange: handleRoleChange,
      isDisabled: ({ role, isEditable, isInherited }) =>
        isScimEnabled ||
        !isEditable ||
        isInherited ||
        Boolean(ROLES_CONTROLLED_BY_ROOT.includes(role) && parsedParams.tenantId !== rootId),
      width: getRoleGridColumnWidth(t(roleTexts[columnRole].labelKey)),
    })
  );

  const gridColumns = [personColumn(t), ...roleColumns];
  const rows: Row[] = users.map(({ userId, email, firstName, lastName, roles, editableRoles }) => ({
    id: userId,
    email,
    firstName,
    lastName,
    roles,
    editableRoles,
  }));

  return (
    <PageWrapper sx={{ px: 0, pb: 0 }}>
      {(() => {
        if (!updateRoleOnTenant.data || updateRoleOnTenant.data.fulfilled) return null;

        const { reason } = updateRoleOnTenant.data;
        if (reason === "NO_RESPONSIBLE_PERMISSION_ON_OTHER_ROLE") {
          return <UserResponsibleWarningDialog onClose={updateRoleOnTenant.reset} />;
        }

        if (reason === "LOSE_PERMISSION_TO_UPDATE_ROLES") {
          return (
            <LosePermissionToUpdateRolesDialog
              onCancel={updateRoleOnTenant.reset}
              onConfirm={() =>
                updateRoleOnTenant.mutateAsync({
                  ...updateRoleOnTenant.variables,
                  payload: { ...updateRoleOnTenant.variables.payload, force: true },
                })
              }
            />
          );
        }

        logUnhandled(reason, "reason");
      })()}
      <Stack>
        <Stack
          direction="row"
          sx={{
            px: PAGE_PADDING_X,
            pb: 4,
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: 3,
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" sx={{ alignItems: "center", gap: 3 }}>
            <PageTitle title={tenantName} />
            {badge}
          </Stack>

          {!isScimEnabled && (
            <Stack spacing={2} direction="row">
              <CreateUserButton />
            </Stack>
          )}
        </Stack>
        <Box>
          <Grid
            getRowHeight={() => "auto"}
            rows={rows}
            columns={gridColumns}
            disableColumnMenu
            initialState={{ pinnedColumns: { left: ["email"] } }}
            slots={{ footer: GridFooter }}
            partsSx={{ mainSx: { borderLeft: "none" } }}
            sx={{ height: `calc(100dvh - ${HEADER_HEIGHT} - ${PAGE_PADDING_Y})` }}
            disableRowSelectionOnClick
          />
        </Box>
      </Stack>
    </PageWrapper>
  );
};

const GridFooter = () => (
  <Grid.Footer>
    <Grid.NumberOfRows textKey="OrganisationUnitUsers.NumberOfRows" />
  </Grid.Footer>
);

type Row = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: UserRole[];
  editableRoles: Role[];
};

const personColumn = (t: (translation: TranslationKey) => string): GridColDef<Row> => ({
  width: 300,
  field: "email",
  sortable: true,
  headerAlign: "left",
  headerName: "email",
  renderHeader: () => (
    <Typography component="span" sx={{ fontSize: theme => theme.typography.pxToRem(12), fontWeight: "600" }}>
      {t("OrganisationUnitUsers.UserHeader")}
    </Typography>
  ),
  renderCell: ({ row }) => {
    return (
      <Stack direction="row" sx={{ gap: 2, py: 2, alignItems: "flex-start" }}>
        <PersonIcon2 size="sm" />
        <Stack sx={{ gap: 0.1 }}>
          <Typography
            sx={[
              {
                lineHeight: 1,
                fontSize: theme => theme.typography.pxToRem(14),
                fontWeight: "600",
              },
              multilineEllipsis(2),
            ]}
          >
            {row.firstName} {row.lastName}
          </Typography>
          <Typography
            sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.tertiary }}
          >
            {row.email}
          </Typography>
        </Stack>
      </Stack>
    );
  },
});

const roleColumn = ({
  columnRole,
  onRoleChange,
  width,
  isDisabled,
}: {
  columnRole: DisplayedRole;
  onRoleChange: OnRoleChange;
  width: number;
  isDisabled: (params: { role: Role; isEditable: boolean; isInherited: boolean }) => boolean;
}): GridColDef<Row> => ({
  width,
  field: columnRole,
  sortable: false,
  headerName: columnRole,
  headerAlign: "center",
  renderHeader: () => <RoleGridColumnHeader role={columnRole} />,
  renderCell: ({ row }) => {
    const userRole = row.roles.find(({ role }) => columnRole === role);

    return (
      <Stack sx={{ alignItems: "center", width: "100%", justifyContent: "center" }}>
        <Checkbox
          size="small"
          onChange={e => onRoleChange({ value: e.target.checked, role: columnRole, userId: row.id })}
          checked={Boolean(userRole)}
          disabled={isDisabled({
            role: columnRole,
            isEditable: row.editableRoles.includes(columnRole),
            isInherited: Boolean(userRole?.inherited),
          })}
        />
      </Stack>
    );
  },
});
